import {
  ObjectId,
  Collection,
  Document as MongoDBDocument,
  MongoClient,
  Db,
  Filter,
} from "mongodb";
import type { EmbeddingsInterface } from "@langchain/core/embeddings";
import {
  MaxMarginalRelevanceSearchOptions,
  VectorStore,
} from "@langchain/core/vectorstores";
import { Document, DocumentInterface } from "@langchain/core/documents";
import { maximalMarginalRelevance } from "@langchain/core/utils/math";
import { getEnvironmentVariable } from "@langchain/core/utils/env";

/** Azure DocumentDB Similarity type. */
export const AzureDocumentDBSimilarityType = {
  /** Cosine similarity */
  COS: "COS",
  /** Inner - product */
  IP: "IP",
  /** Euclidian distance */
  L2: "L2",
} as const;

/** Azure DocumentDB Similarity type. */
export type AzureDocumentDBSimilarityType =
  (typeof AzureDocumentDBSimilarityType)[keyof typeof AzureDocumentDBSimilarityType];

/** Azure DocumentDB Index Options. */
export type AzureDocumentDBIndexOptions = {
  /** Skips automatic index creation. */
  readonly skipCreate?: boolean;

  readonly indexType?: "ivf" | "hnsw" | "diskann";
  /** Number of clusters that the inverted file (IVF) index uses to group the vector data. */
  readonly numLists?: number;
  /** Number of dimensions for vector similarity. */
  readonly dimensions?: number;
  /** Similarity metric to use with the IVF index. */
  readonly similarity?: AzureDocumentDBSimilarityType;
  /** The max number of connections per layer with the HNSW index. */
  readonly m?: number;
  /** The size of the dynamic candidate list for constructing the graph with the HNSW index. */
  readonly efConstruction?: number;
  /** Max number of neighbors with the DiskANN index */
  readonly maxDegree?: number;
  /** L value for index building with the DiskANN index */
  readonly lBuild?: number;
  /** L value for index searching with the DiskANN index */
  readonly lSearch?: number;
};

/** Azure DocumentDB delete Parameters. */
export type AzureDocumentDBDeleteParams = {
  /** List of IDs for the documents to be removed. */
  readonly ids?: string | string[];
  /** MongoDB filter object or list of IDs for the documents to be removed. */
  readonly filter?: Filter<MongoDBDocument>;
};

/** Configuration options for the `AzureDocumentDBVectorStore` constructor. */
export interface AzureDocumentDBConfig {
  readonly client?: MongoClient;
  readonly connectionString?: string;
  readonly databaseName?: string;
  readonly collectionName?: string;
  readonly indexName?: string;
  readonly textKey?: string;
  readonly embeddingKey?: string;
  readonly indexOptions?: AzureDocumentDBIndexOptions;
}

/**
 * Azure DocumentDB vector store.
 * To use this, you should have both:
 * - the `mongodb` NPM package installed
 * - a connection string associated with an Azure DocumentDB cluster
 *
 * You do not need to create a database or collection, it will be created
 * automatically.
 *
 * You also need an index on the collection, which is by default created
 * automatically using the `createIndex` method.
 */
export class AzureDocumentDBVectorStore extends VectorStore {
  get lc_secrets(): { [key: string]: string } {
    return {
      connectionString: "AZURE_DOCUMENTDB_CONNECTION_STRING",
    };
  }

  private connectPromise: Promise<void>;

  private initPromise?: Promise<void>;

  private readonly client: MongoClient | undefined;

  private database: Db;

  private collection: Collection<MongoDBDocument>;

  readonly indexName: string;

  readonly textKey: string;

  readonly embeddingKey: string;

  private readonly indexOptions: AzureDocumentDBIndexOptions;

  /**
   * Initializes the AzureDocumentDBVectorStore.
   * Connect the client to the database and create the container, creating them if needed.
   * @returns A promise that resolves when the AzureDocumentDBVectorStore has been initialized.
   */
  initialize: () => Promise<void>;

  _vectorstoreType(): string {
    return "azure_documentdb";
  }

  constructor(
    embeddings: EmbeddingsInterface,
    dbConfig: AzureDocumentDBConfig
  ) {
    super(embeddings, dbConfig);

    const connectionString =
      dbConfig.connectionString ??
      getEnvironmentVariable("AZURE_DOCUMENTDB_CONNECTION_STRING") ??
      getEnvironmentVariable("AZURE_COSMOSDB_MONGODB_CONNECTION_STRING");

    if (!dbConfig.client && !connectionString) {
      throw new Error(
        "AzureDocumentDBVectorStore client or connection string must be set."
      );
    }

    if (!dbConfig.client) {
      this.client = new MongoClient(connectionString!, {
        appName: "langchainjs",
      });
    }

    const client = dbConfig.client || this.client!;
    const databaseName = dbConfig.databaseName ?? "documentsDB";
    const collectionName = dbConfig.collectionName ?? "documents";
    this.indexName = dbConfig.indexName ?? "vectorSearchIndex";
    this.textKey = dbConfig.textKey ?? "textContent";
    this.embeddingKey = dbConfig.embeddingKey ?? "vectorContent";
    this.indexOptions = dbConfig.indexOptions ?? {};

    // Deferring initialization to the first call to `initialize`
    this.initialize = () => {
      if (this.initPromise === undefined) {
        this.initPromise = this.init(
          client,
          databaseName,
          collectionName
        ).catch((error) => {
          console.error(
            "Error during AzureDocumentDBVectorStore initialization:",
            error
          );
        });
      }

      return this.initPromise;
    };
  }

  /**
   * Checks if the specified index name during instance construction exists
   * on the collection.
   * @returns A promise that resolves to a boolean indicating if the index exists.
   */
  async checkIndexExists(): Promise<boolean> {
    await this.initialize();
    const indexes = await this.collection.listIndexes().toArray();
    return indexes.some((index) => index.name === this.indexName);
  }

  /**
   * Deletes the index specified during instance construction if it exists.
   * @returns A promise that resolves when the index has been deleted.
   */
  async deleteIndex(): Promise<void> {
    await this.initialize();
    if (await this.checkIndexExists()) {
      await this.collection.dropIndex(this.indexName);
    }
  }

  /**
   * Creates an index on the collection with the specified index name during
   * instance construction.
   *
   * Setting the numLists parameter correctly is important for achieving good
   * accuracy and performance.
   * Since the vector store uses IVF as the indexing strategy, you should
   * create the index only after you have loaded a large enough sample
   * documents to ensure that the centroids for the respective buckets are
   * fairly distributed.
   *
   * We recommend that numLists is set to documentCount/1000 for up to
   * 1 million documents and to sqrt(documentCount) for more than 1 million
   * documents.
   * As the number of items in your database grows, you should tune numLists
   * to be larger in order to achieve good latency performance for vector
   * search.
   *
   * If you're experimenting with a new scenario or creating a small demo,
   * you can start with numLists set to 1 to perform a brute-force search
   * across all vectors.
   * This should provide you with the most accurate results from the vector
   * search, however be aware that the search speed and latency will be slow.
   * After your initial setup, you should go ahead and tune the numLists
   * parameter using the above guidance.
   * @param numLists This integer is the number of clusters that the inverted
   *    file (IVF) index uses to group the vector data.
   *    We recommend that numLists is set to documentCount/1000 for up to
   *    1 million documents and to sqrt(documentCount) for more than 1 million
   *    documents.
   *    Using a numLists value of 1 is akin to performing brute-force search,
   *    which has limited performance
   * @param indexType Index Type for Azure DocumentDB index.
   * @param dimensions Number of dimensions for vector similarity.
   *    The maximum number of supported dimensions is 2000.
   *    If no number is provided, it will be determined automatically by
   *    embedding a short text.
   * @param similarity Similarity metric to use with the IVF index.
   *    Possible options are:
   *    - AzureDocumentDBSimilarityType.COS (cosine distance)
   *    - AzureDocumentDBSimilarityType.L2 (Euclidean distance)
   *    - AzureDocumentDBSimilarityType.IP (inner product)
   * @returns A promise that resolves when the index has been created.
   */
  async createIndex(
    dimensions: number | undefined = undefined,
    indexType: "ivf" | "hnsw" | "diskann" = "ivf",
    similarity: AzureDocumentDBSimilarityType = AzureDocumentDBSimilarityType.COS
  ): Promise<void> {
    await this.connectPromise;

    let vectorLength = dimensions;

    if (vectorLength === undefined) {
      const queryEmbedding = await this.embeddings.embedQuery("test");
      vectorLength = queryEmbedding.length;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cosmosSearchOptions: any = {
      kind: "",
      similarity,
      dimensions: vectorLength,
    };

    if (indexType === "hnsw") {
      cosmosSearchOptions.kind = "vector-hnsw";
      cosmosSearchOptions.m = this.indexOptions.m ?? 16;
      cosmosSearchOptions.efConstruction =
        this.indexOptions.efConstruction ?? 200;
    } else if (indexType === "diskann") {
      cosmosSearchOptions.kind = "vector-diskann";
      cosmosSearchOptions.maxDegree = this.indexOptions.maxDegree ?? 40;
      cosmosSearchOptions.lBuild = this.indexOptions.lBuild ?? 50;
      cosmosSearchOptions.lSearch = this.indexOptions.lSearch ?? 40;
      /** Default to IVF index */
    } else {
      cosmosSearchOptions.kind = "vector-ivf";
      cosmosSearchOptions.numLists = this.indexOptions.numLists ?? 100;
    }

    const createIndexCommands = {
      createIndexes: this.collection.collectionName,
      indexes: [
        {
          name: this.indexName,
          key: { [this.embeddingKey]: "cosmosSearch" },
          cosmosSearchOptions,
        },
      ],
    };

    await this.database.command(createIndexCommands);
  }

  /**
   * Removes specified documents from the AzureDocumentDBVectorStore.
   * If no IDs or filter are specified, all documents will be removed.
   * @param params Parameters for the delete operation.
   * @returns A promise that resolves when the documents have been removed.
   */
  async delete(
    params: AzureDocumentDBDeleteParams | string[] = {}
  ): Promise<void> {
    await this.initialize();

    let ids: string | string[] | undefined;
    let filter: AzureDocumentDBDeleteParams["filter"];
    if (Array.isArray(params)) {
      ids = params;
    } else {
      ids = params.ids;
      filter = params.filter;
    }
    const idsArray = Array.isArray(ids) ? ids : [ids];
    const deleteIds = ids && idsArray.length > 0 ? idsArray : undefined;
    let deleteFilter = filter ?? {};

    if (deleteIds) {
      const objectIds = deleteIds.map((id) => new ObjectId(id));
      deleteFilter = { _id: { $in: objectIds }, ...deleteFilter };
    }

    await this.collection.deleteMany(deleteFilter);
  }

  /**
   * Closes any newly instantiated Azure DocumentDB client.
   * If the client was passed in the constructor, it will not be closed.
   * @returns A promise that resolves when any newly instantiated Azure
   *     DocumentDB client has been closed.
   */
  async close(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }

  /**
   * Method for adding vectors to the AzureDocumentDBVectorStore.
   * @param vectors Vectors to be added.
   * @param documents Corresponding documents to be added.
   * @returns A promise that resolves to the added documents IDs.
   */
  async addVectors(
    vectors: number[][],
    documents: DocumentInterface[]
  ): Promise<string[]> {
    const docs = vectors.map((embedding, idx) => ({
      [this.textKey]: documents[idx].pageContent,
      [this.embeddingKey]: embedding,
      ...documents[idx].metadata,
    }));
    await this.initialize();
    const result = await this.collection.insertMany(docs);
    return Object.values(result.insertedIds).map((id) => String(id));
  }

  /**
   * Method for adding documents to the AzureDocumentDBVectorStore. It first converts
   * the documents to texts and then adds them as vectors.
   * @param documents The documents to add.
   * @returns A promise that resolves to the added documents IDs.
   */
  async addDocuments(documents: DocumentInterface[]): Promise<string[]> {
    const texts = documents.map(({ pageContent }) => pageContent);
    return this.addVectors(
      await this.embeddings.embedDocuments(texts),
      documents
    );
  }

  /**
   * Method that performs a similarity search on the vectors stored in the
   * collection. It returns a list of documents and their corresponding
   * similarity scores.
   * @param queryVector Query vector for the similarity search.
   * @param k=4 Number of nearest neighbors to return.
   * @returns Promise that resolves to a list of documents and their corresponding similarity scores.
   */
  async similaritySearchVectorWithScore(
    queryVector: number[],
    k: number,
    indexType?: "ivf" | "hnsw" | "diskann"
  ): Promise<[Document, number][]> {
    await this.initialize();

    const pipeline = [
      {
        $search: {
          cosmosSearch: {
            vector: queryVector,
            path: this.embeddingKey,
            k: k ?? 4,
            ...(indexType === "diskann"
              ? { lSearch: this.indexOptions.lSearch ?? 40 }
              : {}),
          },
          returnStoredSource: true,
        },
      },
      {
        $project: {
          similarityScore: { $meta: "searchScore" },
          document: "$$ROOT",
        },
      },
    ];
    const results = await this.collection
      .aggregate(pipeline)
      .map<[Document, number]>((result) => {
        const { similarityScore: score, document } = result;
        const text = document[this.textKey];
        return [new Document({ pageContent: text, metadata: document }), score];
      });

    return results.toArray();
  }

  /**
   * Return documents selected using the maximal marginal relevance.
   * Maximal marginal relevance optimizes for similarity to the query AND
   * diversity among selected documents.
   * @param query Text to look up documents similar to.
   * @param options.k Number of documents to return.
   * @param options.fetchK=20 Number of documents to fetch before passing to
   *     the MMR algorithm.
   * @param options.lambda=0.5 Number between 0 and 1 that determines the
   *     degree of diversity among the results, where 0 corresponds to maximum
   *     diversity and 1 to minimum diversity.
   * @returns List of documents selected by maximal marginal relevance.
   */
  async maxMarginalRelevanceSearch(
    query: string,
    options: MaxMarginalRelevanceSearchOptions<this["FilterType"]>
  ): Promise<Document[]>;

  async maxMarginalRelevanceSearch(
    query: string,
    options: MaxMarginalRelevanceSearchOptions<this["FilterType"]>,
    indexType: "ivf" | "hnsw" | "diskann"
  ): Promise<Document[]>;

  async maxMarginalRelevanceSearch(
    query: string,
    options: MaxMarginalRelevanceSearchOptions<this["FilterType"]>,
    indexType?: "ivf" | "hnsw" | "diskann"
  ): Promise<Document[]> {
    const { k, fetchK = 20, lambda = 0.5 } = options;

    const queryEmbedding = await this.embeddings.embedQuery(query);
    const docs = await this.similaritySearchVectorWithScore(
      queryEmbedding,
      fetchK,
      indexType
    );
    const embeddingList = docs.map((doc) => doc[0].metadata[this.embeddingKey]);

    // Re-rank the results using MMR
    const mmrIndexes = maximalMarginalRelevance(
      queryEmbedding,
      embeddingList,
      lambda,
      k
    );

    const mmrDocs = mmrIndexes.map((index) => docs[index][0]);
    return mmrDocs;
  }

  /**
   * Initializes the AzureDocumentDBVectorStore by connecting to the database.
   * @param client The MongoClient to use for connecting to the database.
   * @param databaseName The name of the database to use.
   * @param collectionName The name of the collection to use.
   * @returns A promise that resolves when the AzureDocumentDBVectorStore has been initialized.
   */
  private async init(
    client: MongoClient,
    databaseName: string,
    collectionName: string
  ): Promise<void> {
    this.connectPromise = (async () => {
      await client.connect();
      this.database = client.db(databaseName);
      this.collection = this.database.collection(collectionName);
    })();

    // Unless skipCreate is set, create the index
    // This operation is no-op if the index already exists
    if (!this.indexOptions.skipCreate) {
      const indexType = this.indexOptions.indexType || "ivf";
      await this.createIndex(
        this.indexOptions.dimensions,
        indexType,
        this.indexOptions.similarity
      );
    }
  }

  /**
   * Static method to create an instance of AzureDocumentDBVectorStore from a
   * list of texts. It first converts the texts to vectors and then adds
   * them to the collection.
   * @param texts List of texts to be converted to vectors.
   * @param metadatas Metadata for the texts.
   * @param embeddings Embeddings to be used for conversion.
   * @param dbConfig Database configuration for Azure DocumentDB.
   * @returns Promise that resolves to a new instance of AzureDocumentDBVectorStore.
   */
  static async fromTexts(
    texts: string[],
    metadatas: object[] | object,
    embeddings: EmbeddingsInterface,
    dbConfig: AzureDocumentDBConfig
  ): Promise<AzureDocumentDBVectorStore> {
    const docs: Document[] = [];
    for (let i = 0; i < texts.length; i += 1) {
      const metadata = Array.isArray(metadatas) ? metadatas[i] : metadatas;
      const newDoc = new Document({
        pageContent: texts[i],
        metadata,
      });
      docs.push(newDoc);
    }
    return AzureDocumentDBVectorStore.fromDocuments(docs, embeddings, dbConfig);
  }

  /**
   * Static method to create an instance of AzureDocumentDBVectorStore from a
   * list of documents. It first converts the documents to vectors and then
   * adds them to the collection.
   * @param docs List of documents to be converted to vectors.
   * @param embeddings Embeddings to be used for conversion.
   * @param dbConfig Database configuration for Azure DocumentDB.
   * @returns Promise that resolves to a new instance of AzureDocumentDBVectorStore.
   */
  static async fromDocuments(
    docs: Document[],
    embeddings: EmbeddingsInterface,
    dbConfig: AzureDocumentDBConfig
  ): Promise<AzureDocumentDBVectorStore> {
    const instance = new this(embeddings, dbConfig);
    await instance.addDocuments(docs);
    return instance;
  }
}

// ============================================================================
// Deprecated aliases for backwards compatibility
// ============================================================================

/**
 * @deprecated Use `AzureDocumentDBSimilarityType` instead. This alias will be removed in a future version.
 */
export const AzureCosmosDBMongoDBSimilarityType = AzureDocumentDBSimilarityType;

/**
 * @deprecated Use `AzureDocumentDBSimilarityType` instead. This alias will be removed in a future version.
 */
export type AzureCosmosDBMongoDBSimilarityType = AzureDocumentDBSimilarityType;

/**
 * @deprecated Use `AzureDocumentDBIndexOptions` instead. This alias will be removed in a future version.
 */
export type AzureCosmosDBMongoDBIndexOptions = AzureDocumentDBIndexOptions;

/**
 * @deprecated Use `AzureDocumentDBDeleteParams` instead. This alias will be removed in a future version.
 */
export type AzureCosmosDBMongoDBDeleteParams = AzureDocumentDBDeleteParams;

/**
 * @deprecated Use `AzureDocumentDBConfig` instead. This alias will be removed in a future version.
 */
export type AzureCosmosDBMongoDBConfig = AzureDocumentDBConfig;

/**
 * @deprecated Use `AzureDocumentDBVectorStore` instead. This alias will be removed in a future version.
 */
export const AzureCosmosDBMongoDBVectorStore = AzureDocumentDBVectorStore;

/**
 * @deprecated Use `AzureDocumentDBVectorStore` instead. This alias will be removed in a future version.
 */
export type AzureCosmosDBMongoDBVectorStore = AzureDocumentDBVectorStore;
